import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import PropTypes from 'prop-types';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
  setIpmaCisProfileCreator,
} from 'store/actionsCreators';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { removeIpmaCisCreator } from '../../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { formatDate } from 'helpers/date';

const LevelDomains = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  removeFilters,
  setProfile,
  removeLevelDomain,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ipma_cis'] }, userPermissions);

  useEffect(() => {
    if (document.getElementById('ipma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.id || ''}
                  onFilter={(val) => handleColumnFilterChange('id', val)}
                  filterPlaceholder={t('page.user.cis.levelDomains.id')}
                  label={t('page.user.cis.levelDomains.id')}
                  sort
                  sortDirection={getSortDirection('id')}
                  onSort={(dir) => handleColumnSort('id', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.level || ''}
                  onFilter={(val) => handleColumnFilterChange('level', val)}
                  filterPlaceholder={t('page.user.cis.levelDomains.levelPlaceholder')}
                  label={t('page.user.cis.levelDomains.level')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.domain || ''}
                  onFilter={(val) => handleColumnFilterChange('domain', val)}
                  filterPlaceholder={t('page.user.cis.levelDomains.domainPlaceholder')}
                  label={t('page.user.cis.levelDomains.domain')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.designation || ''}
                  onFilter={(val) => handleColumnFilterChange('designation', val)}
                  filterPlaceholder={t('page.user.cis.levelDomains.designationPlaceholder')}
                  label={t('page.user.cis.levelDomains.designation')}
                  sort
                  sortDirection={getSortDirection('designation')}
                  onSort={(dir) => handleColumnSort('designation', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.validFrom || ''}
                  onFilter={(val) => handleColumnFilterChange('validFrom', val)}
                  filterPlaceholder={t('page.user.cis.levelDomains.validFromPlaceholder')}
                  label={t('page.user.cis.levelDomains.validFrom')}
                  sort
                  sortDirection={getSortDirection('validFrom')}
                  onSort={(dir) => handleColumnSort('validFrom', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker"
                  filterValue={filters.validTo || ''}
                  onFilter={(val) => handleColumnFilterChange('validTo', val)}
                  filterPlaceholder={t('page.user.cis.levelDomains.validToPlaceholder')}
                  label={t('page.user.cis.levelDomains.validTo')}
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.levelDomains.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={7}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={7}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.levelDomains.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.levelDomains.level')}:</Table.Label>
                      {item.level.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.levelDomains.domain')}:</Table.Label>
                      {item.domain.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.levelDomains.designation')}:</Table.Label>
                      {item.designation}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.levelDomains.validFrom')}:</Table.Label>
                      {item.validFrom ? formatDate(item.validFrom) : ''}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.levelDomains.validTo')}:</Table.Label>
                      {item.validTo ? formatDate(item.validTo) : ''}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.levelDomains.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/ipma/cis/level-domains/edit`);
                            }}
                          />
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setSelectedItem(item);
                              setOpenConfirmModal(true);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.levelDomains.deleteConfirmMessage')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeLevelDomain(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button
            onClick={() => history.push('/ipma/cis/level-domains/create')}
            icon="plus"
            style={{ width: '30%' }}
            secondary
          >
            {t('page.user.cis.levelDomains.addLevelDomain')}
          </Button>,
          document.getElementById('ipma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

LevelDomains.propTypes = {
  loading: PropTypes.bool.isRequired,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  removeFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeLevelDomain: PropTypes.func.isRequired,
};

LevelDomains.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, user }) => ({
  loading: isLoading(loading, 'IPMA_CIS'),
  list: ipma.cis.levelDomains,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('levelDomains')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('levelDomains', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('levelDomains', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('levelDomains', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('levelDomains', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('levelDomains')),
  setProfile: (payload) => dispatch(setIpmaCisProfileCreator('levelDomains', payload)),
  removeLevelDomain: (payload) => dispatch(removeIpmaCisCreator('levelDomains', payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LevelDomains);
